import { useState, useRef } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  Home, Utensils, Droplet, BarChart3, Syringe, History, IdCard,
  Users, Settings as SettingsIcon, Plus, LogOut, ChevronDown, Menu, X, Camera,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { fileToAvatar } from "../lib/avatar";
import Logo from "./Logo";
import DemoBanner from "./DemoBanner";
import RegisterSheet from "./RegisterSheet";
import { useI18n } from "../i18n/I18nContext";
import { LANGUAGES } from "../i18n/translations";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../context/ProfileContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger,
} from "./ui/sheet";

const NAV = [
  { to: "/app", key: "nav_home", Icon: Home, end: true },
  { to: "/app/refeicoes", key: "nav_meals", Icon: Utensils },
  { to: "/app/glicemia", key: "nav_glucose", Icon: Droplet },
  { to: "/app/analise", key: "nav_analysis", Icon: BarChart3 },
  { to: "/app/doses", key: "nav_doses", Icon: Syringe },
  { to: "/app/historico", key: "nav_history", Icon: History },
  { to: "/app/ficha", key: "nav_record", Icon: IdCard },
  { to: "/app/familia", key: "nav_family", Icon: Users },
  { to: "/app/definicoes", key: "nav_settings", Icon: SettingsIcon },
];

function Avatar({ profile, size = "h-8 w-8", text = "text-sm" }) {
  if (profile.avatar_url) {
    return <img src={profile.avatar_url} alt="" className={`${size} shrink-0 rounded-full object-cover border border-[#E3EFF9]`} data-testid="profile-avatar-img" />;
  }
  return <span className={`${size} shrink-0 rounded-full bg-[#1A73E8] text-white flex items-center justify-center font-semibold ${text}`}>{profile.preferred_name?.[0]?.toUpperCase() || "?"}</span>;
}

function ProfileBadge() {
  const { t } = useI18n();
  const { activeProfile, profiles, selectProfile, reload, can } = useProfile();
  const fileRef = useRef(null);
  if (!activeProfile) return null;

  async function onPhoto(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const avatar = await fileToAvatar(f);
      await api.patch(`/profiles/${activeProfile.id}`, { avatar });
      await reload();
      toast.success("Foto atualizada");
    } catch {
      toast.error("Não foi possível atualizar a foto");
    }
  }

  return (
    <>
    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-photo-input" />
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full bg-white border border-[#E3EFF9] pl-1 pr-3 py-1 shadow-sm min-w-0 max-w-[60vw]" data-testid="monitored-profile">
          <Avatar profile={activeProfile} />
          <span className="text-left leading-tight min-w-0">
            <span className="block text-[10px] text-[#627D98]">{t("monitoring")}</span>
            <span className="block text-sm font-semibold text-[#0D253F] truncate">{activeProfile.preferred_name}</span>
          </span>
          <ChevronDown size={15} className="text-[#627D98] shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {profiles.map((p) => (
          <DropdownMenuItem key={p.id} onClick={() => selectProfile(p.id)} data-testid={`switch-profile-${p.id}`} className="gap-2">
            <Avatar profile={p} size="h-6 w-6" text="text-xs" />
            {p.preferred_name}{p.is_demo ? " · demo" : ""}
          </DropdownMenuItem>
        ))}
        {can("edit_profile") && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => fileRef.current?.click()} className="gap-2" data-testid="change-profile-photo">
              <Camera size={15} className="text-[#1A73E8]" /> Alterar foto de {activeProfile.preferred_name?.split(" ")[0]}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}

function LanguagePicker() {
  const { lang, setLang } = useI18n();
  const cur = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1 rounded-full border border-[#E3EFF9] bg-white px-3 py-1.5 text-sm font-semibold text-[#3B526B]" data-testid="language-picker">
          {cur.flag} <ChevronDown size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}>
            {l.flag} · {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { t } = useI18n();
  const { logout } = useAuth();
  const { activeFamily, can } = useProfile();
  const canLog = ["log_meals", "log_glucose", "log_doses"].some((p) => can(p));
  const navigate = useNavigate();
  const location = useLocation();
  const [regOpen, setRegOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);

  const bottomTabs = [
    { to: "/app", key: "nav_home", Icon: Home, end: true },
    { to: "/app/analise", key: "nav_analysis", Icon: BarChart3 },
    { reg: true },
    { to: "/app/historico", key: "nav_history", Icon: History },
    { to: "/app/familia", key: "nav_family", Icon: Users },
  ];

  async function doLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="min-h-screen bg-[#F4F8FC] flex overflow-x-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-[264px] shrink-0 bg-white border-r border-[#E3EFF9] fixed inset-y-0 left-0 z-30">
        <div className="px-5 py-5 border-b border-[#E3EFF9]"><Logo /></div>
        <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive ? "bg-[#1A73E8] text-white shadow-sm" : "text-[#3B526B] hover:bg-[#EBF3FA]"
                }`
              }
            >
              <n.Icon size={19} /> {t(n.key)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E3EFF9]">
          <button onClick={doLogout} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[#C5221F] hover:bg-[#FCE8E6] w-full" data-testid="logout-btn">
            <LogOut size={18} /> {t("logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 lg:ml-[264px] flex flex-col min-h-screen pb-20 lg:pb-0">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-[#E3EFF9] px-4 lg:px-8 py-3 flex items-center gap-3">
          {/* Mobile menu */}
          <Sheet open={mobileNav} onOpenChange={setMobileNav}>
            <SheetTrigger asChild>
              <button className="lg:hidden text-[#3B526B]" data-testid="mobile-menu-btn" aria-label="Menu"><Menu size={24} /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0">
              <div className="px-5 py-5 border-b border-[#E3EFF9] flex items-center justify-between">
                <Logo />
              </div>
              <nav className="px-3 py-4 space-y-1">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileNav(false)}
                    data-testid={`mobilenav-${n.key}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${
                        isActive ? "bg-[#1A73E8] text-white" : "text-[#3B526B] hover:bg-[#EBF3FA]"
                      }`
                    }>
                    <n.Icon size={19} /> {t(n.key)}
                  </NavLink>
                ))}
                <button onClick={doLogout} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[#C5221F] w-full mt-2">
                  <LogOut size={18} /> {t("logout")}
                </button>
              </nav>
            </SheetContent>
          </Sheet>

          <div className="lg:hidden shrink-0"><Logo size={30} withText={false} /></div>
          <ProfileBadge />
          <div className="ml-auto shrink-0"><LanguagePicker /></div>
        </header>

        <main className="flex-1 min-w-0 px-4 lg:px-8 py-5 max-w-[1280px] w-full mx-auto df-rise" key={location.pathname}>
          <DemoBanner isDemo={activeFamily?.is_demo} />
          <div className="mt-4"><Outlet context={{ openRegister: () => setRegOpen(true) }} /></div>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#E3EFF9] h-16 flex items-center justify-around px-2">
        {bottomTabs.map((tab, i) =>
          tab.reg ? (canLog ? (
            <button
              key="reg"
              onClick={() => setRegOpen(true)}
              data-testid="register-fab"
              className="h-14 w-14 -mt-6 rounded-full bg-[#1A73E8] text-white flex items-center justify-center shadow-lg shadow-blue-500/30 active:scale-95 transition"
              aria-label={t("register_action")}
            >
              <Plus size={26} />
            </button>
          ) : null) : (
            <NavLink key={tab.to} to={tab.to} end={tab.end}
              data-testid={`bottomnav-${tab.key}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 text-[10px] font-medium px-2 ${
                  isActive ? "text-[#1A73E8]" : "text-[#627D98]"
                }`
              }>
              <tab.Icon size={21} /> {t(tab.key)}
            </NavLink>
          )
        )}
      </nav>

      {/* Desktop floating register */}
      {canLog && (
      <button
        onClick={() => setRegOpen(true)}
        data-testid="register-fab-desktop"
        className="hidden lg:flex fixed bottom-7 right-7 z-30 items-center gap-2 rounded-full bg-[#1A73E8] hover:bg-[#1557B0] text-white px-5 py-3.5 font-semibold shadow-lg shadow-blue-500/30 active:scale-95 transition"
      >
        <Plus size={20} /> {t("register_action")}
      </button>
      )}

      <RegisterSheet open={regOpen} onOpenChange={setRegOpen} onDone={() => window.dispatchEvent(new Event("df-refresh"))} />
    </div>
  );
}
